import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Icons,
  iconSize,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { OPEN_VISIT_STATUSES, SAFETY_CONFIG } from '@munifocus/contracts-gov';
import { useAuth } from '../../providers/AuthProvider';
import { useVisits } from '../../lib/visits-api';
import { useMutationToast } from '../../lib/mutation-toast';
import { useConfirmAction } from '../../lib/useConfirmAction';
import {
  formatCountdown,
  shortTime,
  useCheckIn,
  useEndTimer,
  useSafetyStatus,
  useStartTimer,
} from '../../lib/safety-api';

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/**
 * The lone-worker check-in timer for a visit to this file.
 *
 * The officer says how long they expect to be; the server alerts their
 * supervisors if they have not checked in by then plus the municipality's
 * grace. Shown only where the file's municipality has officer safety on. One
 * timer per officer: one running on another file is named, with a link to it.
 */
export function SafetyTimerPanel({
  fileId,
  localGovernmentId,
}: {
  fileId: string;
  localGovernmentId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const toast = useMutationToast();
  const confirm = useConfirmAction();
  const status = useSafetyStatus(!!user);
  const visits = useVisits(fileId);
  const start = useStartTimer();
  const checkIn = useCheckIn();
  const end = useEndTimer();
  const [minutes, setMinutes] = useState<number>(SAFETY_CONFIG.presetMinutes[1] ?? 30);
  const now = useNow();

  const lg = status.data?.municipalities.find((m) => m.localGovernmentId === localGovernmentId);
  if (!lg?.enabled) return null;

  const timer = status.data?.timer ?? null;
  const ownVisit = (visits.data ?? []).find(
    (v) => v.officerId === user?.id && OPEN_VISIT_STATUSES.includes(v.status),
  );
  const busy = start.isPending || checkIn.isPending || end.isPending;

  const header = (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
      <Icons.Timer size={iconSize.md} aria-hidden />
      <Typography id="safety-timer-title" variant="overline">
        {t('gov.safety.timer.title')}
      </Typography>
    </Stack>
  );

  if (timer && timer.fileId !== fileId) {
    return (
      <Box component="section" aria-labelledby="safety-timer-title">
        {header}
        <Alert severity="info">
          {t('gov.safety.timer.elsewhere', { reference: timer.fileReference })}{' '}
          <Link component={RouterLink} to={`/files/${timer.fileId}`}>
            {timer.fileReference}
          </Link>
        </Alert>
      </Box>
    );
  }

  if (timer) {
    const left = Date.parse(timer.dueAt) - now;
    const overdue = timer.status === 'overdue' || left < 0;
    const doCheckIn = (extendMinutes?: number) =>
      checkIn.mutate(
        { timerId: timer.id, ...(extendMinutes ? { extendMinutes } : {}) },
        {
          onSuccess: (next) =>
            toast.success('gov.safety.timer.checkedIn', {
              time: shortTime(next.dueAt, i18n.language),
            }),
          onError: (err) => toast.error(err, 'gov.safety.timer.failed'),
        },
      );
    const doEnd = async () => {
      const ok = await confirm({
        title: t('gov.safety.timer.endConfirmTitle'),
        body: t('gov.safety.timer.endConfirmBody'),
        confirmLabel: t('gov.safety.timer.endConfirmAction'),
      });
      if (!ok) return;
      end.mutate(timer.id, {
        onSuccess: () => toast.success('gov.safety.timer.ended'),
        onError: (err) => toast.error(err, 'gov.safety.timer.failed'),
      });
    };
    return (
      <Box component="section" aria-labelledby="safety-timer-title">
        {header}
        {timer.status === 'overdue' && (
          <Alert severity="error" role="alert" sx={{ mb: 1.5 }}>
            {t('gov.safety.timer.overdue')}
          </Alert>
        )}
        <Typography sx={{ fontWeight: 700 }}>
          {t('gov.safety.timer.running', { time: shortTime(timer.dueAt, i18n.language) })}
        </Typography>
        <Typography
          variant="body2"
          color={overdue ? 'error' : 'text.secondary'}
          aria-live="off"
          data-testid="timer-countdown"
        >
          {left >= 0
            ? t('gov.safety.timer.dueIn', { duration: formatCountdown(left) })
            : t('gov.safety.timer.duePast', { duration: formatCountdown(left) })}
          {' · '}
          {t('gov.safety.timer.alertAt', { time: shortTime(timer.alertAt, i18n.language) })}
        </Typography>
        <Stack direction="row" sx={{ mt: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => doCheckIn()}
            startIcon={<Icons.Check size={iconSize.sm} aria-hidden />}
            title={t('gov.safety.timer.checkInHint')}
          >
            {t('gov.safety.timer.checkIn')}
          </Button>
          {SAFETY_CONFIG.extendMinutes.map((m) => (
            <Button key={m} variant="outlined" disabled={busy} onClick={() => doCheckIn(m)}>
              {t('gov.safety.timer.extendBy', { count: m })}
            </Button>
          ))}
          <Button color="error" disabled={busy} onClick={() => void doEnd()}>
            {t('gov.safety.timer.end')}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box component="section" aria-labelledby="safety-timer-title">
      {header}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('gov.safety.timer.description')}
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ gap: 1.5, alignItems: { sm: 'center' } }}
      >
        <TextField
          select
          size="small"
          label={t('gov.safety.timer.expected')}
          value={minutes}
          onChange={(e) => setMinutes(Number(e.target.value))}
          sx={{ minWidth: 200 }}
        >
          {SAFETY_CONFIG.presetMinutes.map((m) => (
            <MenuItem key={m} value={m}>
              {t('gov.safety.minutes', { count: m })}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() =>
            start.mutate(
              { fileId, visitId: ownVisit?.id ?? null, expectedMinutes: minutes },
              { onError: (err) => toast.error(err, 'gov.safety.timer.failed') },
            )
          }
        >
          {start.isPending ? t('gov.safety.timer.starting') : t('gov.safety.timer.start')}
        </Button>
      </Stack>
    </Box>
  );
}
